import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import {
  Button,
  Card,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Drawer, Popconfirm, Table } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import SearchInput from "../../form-components/SearchComponent";
import ProjectScopeSelect, {
  ORG_MASTER_PROJECT_ID,
} from "../shared/ProjectScopeSelect";
import { useSelector } from "react-redux";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import NormCreation, {
  NormMaterialType,
  NormSubmit,
  wastageFractionToPercent,
} from "./NormCreation";
import NormUploadModal from "./NormUploadModal";
import NormUploadHistoryDrawer from "./NormUploadHistoryDrawer";
import NormsExportModal from "./NormsExportModal";
import { normsApis } from "../../../api/Norms";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { useDispatch } from "react-redux";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  AddAccess,
  EditAccess,
  ViewAccess,
} from "../../../components/accessControl/AccessControl";

const defaultNormData: NormSubmit = {
  normCode: "",
  normTitle: "",
  remarks: "",
  projectId: ORG_MASTER_PROJECT_ID,
  materials: [],
};

function NormList() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  // Attention mark on the History button: amber=generating, green=ready,
  // red=failed. Cleared when the user opens the History drawer's Exports tab.
  const exportStatus: "idle" | "pending" | "ready" | "failed" = useSelector(
    (state: any) => state.highlight?.exportStatus || "idle"
  );
  const exportMark: { color: string; title: string } | null =
    exportStatus === "pending"
      ? { color: "#f4bd0e", title: "An export is generating…" }
      : exportStatus === "ready"
      ? { color: "#1ee0ac", title: "An export is ready to download" }
      : exportStatus === "failed"
      ? { color: "#e85347", title: "An export failed — check History" }
      : null;
  const [viewMaterialsModal, setViewMaterialsModal] = useState<{
    status: boolean;
    title: string;
    materials: NormMaterialType[];
  }>({
    status: false,
    title: "",
    materials: [],
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyDefaultTab, setHistoryDefaultTab] = useState<
    "imports" | "exports"
  >("imports");
  // Async master export in the upload-template format: a contained modal owns
  // the request → poll → download lifecycle (the file is built by a worker and
  // stored in DMS). The master is a growing dataset, so the browser never
  // pages it; once an upsert upload exists the file round-trips as-is.
  const [exportOpen, setExportOpen] = useState(false);

  const toggleNormStatus = async (record: any) => {
    try {
      setLoading(true);
      const apiResponse =
        record?.status === "Active"
          ? await normsApis.deactivateNorm(Number(record?.normId))
          : await normsApis.activateNorm(Number(record?.normId));
      if (apiResponse?.status) {
        refreshData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error updating norm status:", error);
      setLoading(false);
    }
  };

  const columns: any = [
    {
      title: "Norm Group",
      key: "normGroup",
      width: 200,
      render: (_: any, record: any) =>
        record?.groupCode ? (
          <div>
            <div style={{ color: "#022c57" }}>{record.groupName || record.groupCode}</div>
            <span style={{ fontSize: 11.5, color: "#8094ae" }}>
              {record.groupCode}
              {record.level1Code && record.level1Code !== record.groupCode
                ? ` · ${record.level1Code}`
                : ""}
            </span>
          </div>
        ) : (
          <span style={{ color: "#c9d2e3" }}>—</span>
        ),
    },
    {
      title: "Norm Code",
      dataIndex: "normCode",
      key: "normCode",
      width: 200,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`norm-${record?.normId}`}
              onClick={() => {
                setModalOpen({
                  status: true,
                  id: record?.normId,
                  defaultData: { ...defaultNormData },
                });
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`norm-${record?.normId}`}
              direction="right"
              text={"Click To View " + record?.normCode + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "normTitle",
      key: "normTitle",
      width: 250,
      render: (text: any) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Materials",
      dataIndex: "viewMaterials",
      key: "viewMaterials",
      width: 160,
      render: (text: any, record: any) => {
        const materialsLength = record?.normMaterials?.length || 0;
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`viewMaterials-${record?.normId}`}
              onClick={() => {
                setViewMaterialsModal({
                  status: true,
                  title: record?.normCode,
                  materials: record?.normMaterials || [],
                });
              }}
            >
              View Materials &nbsp;
              {`( ${materialsLength} )`}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`viewMaterials-${record?.normId}`}
              direction="right"
              text={"Click To View Norm Materials Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 150,
      render: (text: any) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Updated On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color: `${text == "Active" ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {text == "Active" ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )}{" "}
              {text}
            </h6>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (text: any, record: any) => {
        const isActive = record?.status === "Active";
        return (
          <EditAccess moduleName="Norms">
            <Popconfirm
              title={`${isActive ? "Deactivate" : "Activate"} Norm`}
              description={`Are you sure you want to ${
                isActive ? "deactivate" : "activate"
              } "${record?.normCode}"?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => toggleNormStatus(record)}
            >
              <Button size="sm" color="" className="btn-dim">
                <span
                  className="inlineTableCellLink"
                  style={{ color: isActive ? "red" : "green" }}
                >
                  {isActive ? "Deactivate" : "Activate"}
                </span>
              </Button>
            </Popconfirm>
          </EditAccess>
        );
      },
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  // Scope: which project's rows (0 = organization master). Threaded into every
  // list/create/upload/export/duplicate call. Material has no format split.
  const [selectedProjectId, setSelectedProjectId] = useState<number>(
    ORG_MASTER_PROJECT_ID
  );

  const [modalOpen, setModalOpen] = useState<{
    status: boolean;
    id: any;
    defaultData: NormSubmit;
  }>({
    status: false,
    id: undefined,
    defaultData: { ...defaultNormData },
  });

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      const apiResponse = await normsApis.getNormsList({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        // Explicit override (project-change handler) wins over current state to
        // avoid the stale-closure race when the scope changes and resets page 1.
        projectId: params.projectId ?? selectedProjectId,
      });
      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) =>
          append ? [...prev, ...(list || [])] : list || []
        );
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = () => {
    try {
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.activeProjects));
    refreshListView();
  }, []);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  // Reset to page 1 and reload for a project-scope change.
  const loadScoped = (
    nextProjectId: number
  ) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({
      oldPage: 1,
      current: 1,
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
      projectId: nextProjectId,
    });
  };

  const onProjectChange = (projectId: number) => {
    setSelectedProjectId(projectId);
    loadScoped(projectId);
  };

  // Copy the organization-master set into the selected project (projectId > 0).
  const handleDuplicate = () => {
    if (selectedProjectId <= 0) return;
    Swal.fire({
      icon: "question",
      title: "Duplicate organization master set?",
      text: "This copies the organization-master norms into the selected project.",
      showCancelButton: true,
      confirmButtonText: "Duplicate",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setLoading(true);
        const response = await normsApis.duplicateToProject(selectedProjectId);
        const { status, data, message } = response || {};
        if (status) {
          Swal.fire({
            icon: "success",
            title: "Duplicated Successfully",
            text:
              data?.message || `${data?.insertedCount ?? 0} norm(s) copied.`,
            confirmButtonText: "Ok",
          });
          refreshData();
        } else {
          setLoading(false);
          Swal.fire({
            icon: "error",
            title: "Duplication Failed",
            text: message || "Could not duplicate the organization set.",
            confirmButtonText: "Ok",
          });
        }
      } catch (error) {
        console.error("Error duplicating norms:", error);
        setLoading(false);
      }
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    const navState = location.state as any;
    if (navState?.fromNav) {
      setModalOpen({
        status: false,
        id: undefined,
        defaultData: { ...defaultNormData },
      });
    }
  }, [location.key]);

  return modalOpen?.status ? (
    <>
      <Head title={"Norms | Create | Edit"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <NormCreation
                id={modalOpen?.id}
                close={() => {
                  refreshData();
                  setModalOpen({
                    status: false,
                    id: undefined,
                    defaultData: { ...defaultNormData },
                  });
                }}
                refresh={() => {
                  refreshListView();
                }}
                extra={{
                  defaultValues: modalOpen?.defaultData,
                }}
              />
            </div>
          </div>
        </Card>
      </Content>
    </>
  ) : (
    <>
      <Head title={"Material Norms"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <FormProvider {...methods}>
                <form>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5
                      className="mb-0"
                      style={{ color: "#022c57", fontWeight: 600 }}
                    >
                      Material Norms
                    </h5>
                    <div className="d-flex align-items-center">
                      <span
                        className="me-2 text-soft"
                        style={{ fontWeight: 500 }}
                      >
                        Scope
                      </span>
                      <ProjectScopeSelect
                        value={selectedProjectId}
                        onChange={onProjectChange}
                      />
                    </div>
                  </div>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search Norm Code / Title"
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setsearchQuery(value);
                              setPagination({
                                ...pagination,
                                current: 1,
                              });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300} // Adjust delay as needed
                          />
                        </div>
                      </ul>
                    </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={(ev) => {
                              refreshListView();
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </li>
                      <li className="pt-0 pb-0">
                        <ViewAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setExportOpen(true);
                            }}
                          >
                            <Icon name={"download"} />
                            <span>Export Excel</span>
                          </Button>
                        </ViewAccess>
                      </li>
                      <li className="pt-0 pb-0">
                        <span
                          style={{ position: "relative", display: "inline-block" }}
                        >
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setHistoryDefaultTab("imports");
                              setHistoryDrawerOpen(true);
                            }}
                          >
                            <Icon name={"history"} />
                            <span>History</span>
                          </Button>
                          {exportMark && (
                            <span
                              title={exportMark.title}
                              style={{
                                position: "absolute",
                                top: -3,
                                right: -3,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: exportMark.color,
                                border: "2px solid #fff",
                                boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
                              }}
                            />
                          )}
                        </span>
                      </li>
                      {selectedProjectId > 0 && (
                        <li className="pt-0 pb-0">
                          <AddAccess moduleName="Norms">
                            <Button
                              className="toggle"
                              color="light"
                              size="sm"
                              outline
                              onClick={handleDuplicate}
                            >
                              <Icon name={"copy"} />
                              <span>Duplicate org set → project</span>
                            </Button>
                          </AddAccess>
                        </li>
                      )}
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="light"
                            size="sm"
                            outline
                            onClick={() => {
                              setUploadModalOpen(true);
                            }}
                          >
                            <Icon name={"upload"} />
                            <span>Upload Norms</span>
                          </Button>
                        </AddAccess>
                      </li>
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Norms">
                          <Button
                            className="toggle"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              setModalOpen({
                                status: true,
                                id: null,
                                defaultData: {
                                  ...defaultNormData,
                                  projectId: selectedProjectId,
                                },
                              });
                            }}
                          >
                            <Icon name={"plus"} />
                            <span>Add Norm</span>
                          </Button>
                        </AddAccess>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={columns as SkeletonTableColumnsType[]}
                    >
                      <Table
                        rowKey="normId"
                        columns={columns}
                        dataSource={dataSource}
                        pagination={pagination}
                        onChange={tableOnChange}
                        bordered
                        size="middle"
                        scroll={{
                          x: "max-content",
                        }}
                      />
                    </SkeletonTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>
      <NormUploadModal
        open={uploadModalOpen}
        projectId={selectedProjectId}
        onClose={() => {
          setUploadModalOpen(false);
          refreshListView();
        }}
        onSuccess={refreshListView}
      />
      <NormUploadHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        defaultTab={historyDefaultTab}
      />
      <NormsExportModal
        open={exportOpen}
        projectId={selectedProjectId}
        onClose={() => setExportOpen(false)}
        onViewExports={() => {
          setExportOpen(false);
          setHistoryDefaultTab("exports");
          setHistoryDrawerOpen(true);
        }}
      />
      <Drawer
        title={viewMaterialsModal?.title + " Materials Details"}
        placement="right"
        width={1000}
        onClose={() => {
          setViewMaterialsModal({
            status: false,
            title: "",
            materials: [],
          });
        }}
        open={viewMaterialsModal?.status}
      >
        <div className="p-0">
          <Table
            columns={[
              {
                title: "Material",
                dataIndex: "materialName",
                key: "materialName",
                width: 220,
                render: (text: any, record: any) => {
                  return <span>{text || record?.materialId}</span>;
                },
              },
              {
                title: "Material Code",
                dataIndex: "materialCode",
                key: "materialCode",
                width: 140,
                render: (text: any) => <span>{text || "-"}</span>,
              },
              {
                title: "UOM",
                dataIndex: "uomName",
                key: "uomName",
                width: 120,
                render: (text: any) => <span>{text || "-"}</span>,
              },
              {
                title: "Consumption Norm",
                dataIndex: "consumptionNorm",
                key: "consumptionNorm",
                width: 150,
                render: (value: any) =>
                  value !== undefined && value !== null ? Number(value) : "-",
              },
              {
                title: "Wastage (%)",
                dataIndex: "wastage",
                key: "wastage",
                width: 120,
                render: (value: any) =>
                  value !== undefined && value !== null
                    ? `${wastageFractionToPercent(value)}%`
                    : "-",
              },
              {
                title: "Unit Price (₹)",
                dataIndex: "unitPrice",
                key: "unitPrice",
                width: 120,
                render: (value: any) =>
                  value !== undefined && value !== null ? Number(value) : "-",
              },
              {
                title: "PUC Factor",
                dataIndex: "pucFactor",
                key: "pucFactor",
                width: 110,
                render: (value: any) =>
                  value !== undefined && value !== null ? Number(value) : "-",
              },
            ]}
            dataSource={viewMaterialsModal?.materials}
            rowKey="normMaterialId"
            bordered
            size="small"
          />
        </div>
      </Drawer>
    </>
  );
}

export default NormList;
